import { ApiError } from '@lib/api';
import { GENERIC_ISSUE_KEY } from '@resource-kit/controllers';
import type { Issue } from '@resource-kit/model';

/**
 * The host's half of the resource-kit issue contract: turn THIS backend's
 * 400/422 body into the package's `Issue` shape.
 *
 * `normalizeBackendIssues` never moved into `@resource-kit/controllers` and
 * could not: it reads `ApiError` — this app's HTTP client's error class — off a
 * real response, and the wire envelope it unpacks
 * (`error.extras.issues[]`, dot-joined `path`,
 * `backend/src/platform/http/app-factory.ts::createApp`) is this app's, not a property
 * of a resource controller. The package's issue vocabulary is what both sides
 * agree on; translating one backend's 422 into it is the host's half of that
 * agreement (ADR-0035 D1), exactly as the list envelope is.
 *
 * It lived under `src/resource-kit/ui/form/` while the three validation layers
 * around it were still being re-exported from there. #2170 deleted those shims
 * and the directory with them, so the adapter came here — `src/lib/`, next to
 * the `api` module whose error class it reads.
 */

/**
 * Backend zod codes → i18n keys. Closed map with an explicit fallback: an
 * unmapped code degrades to the generic key rather than leaking the backend's
 * English `message` into the UI.
 */
const BACKEND_ISSUE_MESSAGE_KEY: Readonly<Record<string, string>> = {
  invalid_type: 'resource.validation.invalidType',
  too_small: 'resource.validation.tooSmall',
  too_big: 'resource.validation.tooBig',
  invalid_value: 'resource.validation.invalidOption',
  invalid_format: 'resource.validation.invalidFormat',
  unrecognized_keys: 'resource.validation.unrecognizedField',
};

/**
 * Request-part prefixes hono-openapi puts in front of a body field path. They
 * address the HTTP envelope, not the record, so they are dropped before the
 * path becomes a `FieldPath`.
 */
const REQUEST_PART_SEGMENTS = new Set(['body', 'json', 'form', 'query', 'param', 'header']);

interface WireIssue {
  path?: unknown;
  code?: unknown;
  message?: unknown;
}

const isWireIssue = (value: unknown): value is WireIssue =>
  typeof value === 'object' && value !== null;

/**
 * Normalise a backend 400/422 into the same `Issue` shape the frontend layers
 * produce. The envelope is `error.extras.issues[]` with a dot-joined `path`
 * (backend/src/platform/http/app-factory.ts::createApp).
 */
export function normalizeBackendIssues<T>(error: unknown): Issue<T>[] {
  if (!(error instanceof ApiError)) return [];
  const raw = error.extras?.issues;
  if (!Array.isArray(raw)) return [];

  const issues: Issue<T>[] = [];
  for (const entry of raw) {
    if (!isWireIssue(entry) || typeof entry.path !== 'string') continue;
    const segments = entry.path.split('.').filter((s) => s.length > 0);
    while (segments.length > 1 && REQUEST_PART_SEGMENTS.has(segments[0]!)) segments.shift();
    if (segments.length === 0) continue;

    const code = typeof entry.code === 'string' ? entry.code : 'invalid';
    issues.push({
      path: segments as unknown as Issue<T>['path'],
      code,
      message: BACKEND_ISSUE_MESSAGE_KEY[code] ?? GENERIC_ISSUE_KEY,
    });
  }
  return issues;
}
